"use client";

import axios, { AxiosError } from "axios";
import { TOKEN_KEY } from "@/constants/auth";
import type { ForumAd, ForumTopicCard, ForumTopicDetail, Paginated } from "@/types/forum";

const V1 = process.env.NEXT_PUBLIC_API_URL;
const V2 = process.env.NEXT_PUBLIC_API_URL_V2;

const http = axios.create({ headers: { Accept: "application/json" } });

http.interceptors.request.use((config) => {
    if (typeof window !== "undefined") {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export type ForumError = { status?: number; code?: string; message: string };

export function forumError(err: unknown): ForumError {
    const e = err as AxiosError<{ message?: string; code?: string; errors?: Record<string, string[]> }>;
    const data = e?.response?.data;
    const firstValidation = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined;
    return {
        status: e?.response?.status,
        code: data?.code,
        message: firstValidation || data?.message || "خطایی رخ داد. دوباره تلاش کنید.",
    };
}

export function toast(message: string) {
    window.dispatchEvent(new CustomEvent("forum:toast", { detail: message }));
}

export const forumApi = {
    topics: (params: Record<string, string | number | undefined>) =>
        http.get<Paginated<ForumTopicCard>>(`${V2}/forum/topics`, { params }).then((r) => r.data),
    ads: (params: Record<string, string | number | undefined>) =>
        http.get<Paginated<ForumAd>>(`${V2}/forum/ads`, { params }).then((r) => r.data),
    topic: (id: number) => http.get<ForumTopicDetail>(`${V2}/forum/topics/${id}`).then((r) => r.data),
    hit: (id: number) => http.post(`${V2}/forum/topics/${id}/view`).catch(() => undefined),
    adPhone: (slug: string) =>
        http.get<{ phone: string }>(`${V2}/forum/ads/${encodeURIComponent(slug)}/phone`).then((r) => r.data.phone),

    createTopic: (p: { title: string; body: string; category_id: number; tags: string }) =>
        http.post<{ id: number; url: string }>(`${V1}/forum/topics`, p).then((r) => r.data),
    createAnswer: (topicId: number, body: string) =>
        http.post(`${V1}/forum/topics/${topicId}/answers`, { body }).then((r) => r.data),
    voteTopic: (id: number) =>
        http.post<{ voted: boolean; votes: number }>(`${V1}/forum/topics/${id}/vote`).then((r) => r.data),
    voteAnswer: (id: number) =>
        http.post<{ voted: boolean; votes: number }>(`${V1}/forum/answers/${id}/vote`).then((r) => r.data),
    markBest: (answerId: number) => http.post(`${V1}/forum/answers/${answerId}/best`).then((r) => r.data),
    report: (p: { topic_id?: number; answer_id?: number; ad_id?: number; reason: string; details?: string }) =>
        http.post<{ message: string }>(`${V1}/forum/report`, p).then((r) => r.data),

    /** ثبت آگهی با تصویر (multipart) */
    createAd: (form: FormData) =>
        http
            .post<{ id: number; url: string }>(`${V1}/forum/ads`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((r) => r.data),
};
